import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import { useState, useEffect } from "react";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";

export default function HolidayTab() {
  const [globalHolidays, setGlobalHolidays] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [filters, setFilters] = useState({
    name: "",
    branch_id: "all",
  });
  const [filterKey, setFilterKey] = useState(0);
  const [addHolidayForm, setAddHolidayForm] = useState({
    id: "",
    name: "",
    description: "",
    start_date: "",
    end_date: "",
    selectedBranches: [],
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });
  const [error, setError] = useState({
    nameError: "",
    descriptionError: "",
    startDateError: "",
    dateError: "",
    selectedBranchError: "",
  });
  const [loading, setLoading] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const perms = getPermissions();
    if (perms) {
      setPermissions(perms);
    }
  }, []);
  // handle page change
  const handlePageChange = (selectedItem) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: selectedItem.selected + 1,
    }));
  };

  const handlePerPageChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      perPage: parseInt(e.target.value),
      currentPage: 1,
    }));
  };

  // Fetch branches and holidays
  const fetchHolidayData = async () => {
    try {
      setShowSkeleton(true);

      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        ...filters,
      };

      // Fetch branches
      const branchesRes = await apiUrl.get("/getBranches");
      if (branchesRes?.status) {
        setBranches(branchesRes.data.data || []);
      }

      // Fetch holidays
      const holidaysRes = await apiUrl.get("/hr/policies/fetch-holidays", {
        params,
      });
      if (holidaysRes?.status) {
        setGlobalHolidays(holidaysRes?.data?.data || []);
        if (holidaysRes?.data?.pagination) {
          setPagination((prev) => ({
            ...prev,
            total: holidaysRes?.data?.pagination?.total,
            lastPage: holidaysRes?.data?.pagination?.lastPage,
            perPage: holidaysRes?.data?.pagination?.perPage,
            currentPage: holidaysRes?.data?.pagination?.currentPage,
          }));
        }
      }
    } catch (error) {
      console.error("Failed to load holiday data", error);
    } finally {
      setShowSkeleton(false);
    }
  };

  useEffect(() => {
    fetchHolidayData();
  }, [pagination.currentPage, pagination.perPage, filterKey]);

  // Add holiday
  const handleAddHoliday = async () => {
    let hasError = false;
    const newErrors = {
      nameError: "",
      descriptionError: "",
      startDateError: "",
      selectedBranchError: "",
    };

    if (!addHolidayForm.name.trim()) {
      newErrors.nameError = "Holiday name is required";
      hasError = true;
    }
    if (!addHolidayForm.description.trim()) {
      newErrors.descriptionError = "Description is required";
      hasError = true;
    }
    if (!addHolidayForm.start_date) {
      newErrors.startDateError = "Holiday start date is required";
      hasError = true;
    }
    if (addHolidayForm.selectedBranches.length === 0) {
      newErrors.selectedBranchError = "Please select at least one branch";
      hasError = true;
    }
    if (
      addHolidayForm.end_date &&
      new Date(addHolidayForm.end_date) < new Date(addHolidayForm.start_date)
    ) {
      setError((prev) => ({
        ...prev,
        dateError: "End date cannot be before start date",
      }));
      return;
    }

    if (hasError) {
      setError(newErrors);
      return;
    }

    setLoading(true);

    try {
      let response;
      if (editingHoliday) {
        response = await apiUrl.post(
          `/hr/policies/edit-holiday/${addHolidayForm.id}`,
          {
            name: addHolidayForm.name.trim(),
            description: addHolidayForm.description.trim() || "No description",
            start_date: addHolidayForm.start_date,
            end_date: addHolidayForm.end_date || null,
            branch_ids: addHolidayForm.selectedBranches,
          }
        );
      } else {
        response = await apiUrl.post(`/hr/policies/add-holiday`, {
          name: addHolidayForm.name.trim(),
          description: addHolidayForm.description.trim() || "No description",
          start_date: addHolidayForm.start_date,
          end_date: addHolidayForm.end_date || null,
          branch_ids: addHolidayForm.selectedBranches,
        });
      }

      if (response.data.status) {
        Swal.fire({
          title: editingHoliday ? "Edit" : "Save",
          text: editingHoliday
            ? "Holiday updated successfully"
            : "Holiday Saved successfully",
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        setAddHolidayForm({
          id: "",
          name: "",
          description: "",
          start_date: "",
          end_date: "",
          selectedBranches: [],
        });
        setShowAddForm(false);
        setEditingHoliday(null);
        fetchHolidayData();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Delete holiday
  const handleDeleteHoliday = async (holidayId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await apiUrl.delete(
            `/hr/policies/delete-holiday/${holidayId}`
          );

          if (response.data.status) {
            Swal.fire({
              title: "Deleted!",
              text: response.data.message,
              icon: "success",
              timer: 3000,
              toast: true,
              position: "top-end",
              showConfirmButton: false,
            });
            fetchHolidayData();
          }
        } catch (error) {
          Swal.fire(
            "Error!",
            error.response?.data?.message || "Failed to delete campaign.",
            "error"
          );
        }
      }
    });
  };

  // Toggle branch selection for add form
  const handleBranchSelection = (branchId, checked) => {
    setAddHolidayForm((prev) => ({
      ...prev,
      selectedBranches: checked
        ? [...prev.selectedBranches, branchId]
        : prev.selectedBranches.filter((id) => id !== branchId),
    }));
  };

  // Get branch name by ID
  const getBranchName = (branchId) => {
    const branch = branches.find((b) => b.id === branchId);
    return branch ? branch.agencyname || branch.name : `Branch ${branchId}`;
  };

  // Get branch names for display
  const getBranchNames = (branchIds) => {
    return branchIds.map((id) => getBranchName(id));
  };

  const clearFilters = () => {
    setFilters({
      name: "",
      branch_id: "",
    });

    setFilterKey((prev) => prev + 1);

    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const applyFilters = () => {
    setFilterKey((prev) => prev + 1);

    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  // handle filters
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "—";

    const date = new Date(dateString);
    if (isNaN(date)) return "—";

    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  // Skeleton Loader (Updated)
  const SkeletonCard = () => (
    <div className="card border mb-3 rounded-3 shadow-sm">
      <div className="card-body">
        {/* Title + Date */}
        <div className="d-flex align-items-center gap-3 mb-2">
          <h6 className="card-title placeholder-glow mb-0" style={{ flex: 1 }}>
            <span className="placeholder col-6"></span>
          </h6>
          <div className="placeholder-glow">
            <span className="placeholder col-3"></span>
          </div>
        </div>

        {/* Description */}
        <div className="placeholder-glow mb-2">
          <span className="placeholder col-8"></span>
        </div>

        {/* Branch Badges */}
        <div className="d-flex flex-wrap gap-2">
          <span className="badge bg-secondary placeholder col-2">&nbsp;</span>
          <span className="badge bg-secondary placeholder col-2">&nbsp;</span>
          <span className="badge bg-secondary placeholder col-2">&nbsp;</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {permissions["manage_policies"] === 1 && (
        <div className="container-fluid px-2 py-3">
          {/* Header */}
          <div className="card border mb-1">
            <div className="card-body d-flex justify-content-between align-items-center">
              <h5 className="card-title d-flex align-items-center gap-2 mb-0">
                <FaIcons.FaCalendarAlt className="fs-5" />
                Holiday Management
              </h5>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setAddHolidayForm({
                    name: "",
                    description: "",
                    start_date: "",
                    end_date: "",
                    selectedBranches: [],
                  });
                  setError({
                    nameError: "",
                    startDateError: "",
                    dateError: "",

                    descriptionError: "",
                    selectedBranchError: "",
                  });
                  setShowAddForm(true);
                  setEditingHoliday(false);
                }}
              >
                <FaIcons.FaPlus className="me-1" />
                Add Holiday
              </button>
            </div>
          </div>

          {/* Add Holiday Form */}
          {showAddForm && (
            <>
              {/* Backdrop */}
              <div
                className="modal-backdrop fade show"
                style={{
                  display: "block",
                  backgroundColor: "rgba(0,0,0,0.45)",
                  zIndex: 1040,
                }}
                onClick={() => setShowAddForm(false)}
              />
              <div
                className="modal fade show d-block"
                tabIndex={-1}
                role="dialog"
                style={{ zIndex: 1050 }}
              >
                <div
                  className="modal-dialog modal-dialog-centered modal-lg"
                  style={{ maxWidth: 700 }}
                >
                  <div
                    className="modal-content border-0 rounded-4 shadow"
                    style={{ overflow: "hidden" }}
                  >
                    <div
                      className="modal-header"
                      style={{
                        background: "#f7f8fa",
                        borderBottom: "1px solid #eef2f6",
                      }}
                    >
                      <div>
                        <h5 className="modal-title" style={{ fontWeight: 700 }}>
                          {editingHoliday ? "Edit Holiday" : "Add Holiday"}
                        </h5>
                        <div className="text-muted small">
                          Add holiday for branches
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowAddForm(false)}
                      />
                    </div>

                    {/* Modal Body */}
                    <div className="modal-body">
                      <div className="d-flex flex-column gap-4 px-4 py-3">
                        {/* holiday name */}
                        <div className="d-flex gap-4">
                          <div style={{ flex: 1 }}>
                            <label className="form-label">
                              Holiday Name
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              required
                              type="text"
                              className={`form-control ${
                                error.nameError ? "is-invalid" : ""
                              }`}
                              value={addHolidayForm.name}
                              onChange={(e) => {
                                setAddHolidayForm((prev) => ({
                                  ...prev,
                                  name: e.target.value,
                                }));
                                if (error.nameError) {
                                  setError((prev) => ({
                                    ...prev,
                                    nameError: "",
                                  }));
                                }
                              }}
                              placeholder="Enter holiday name"
                            />
                            {error.nameError && (
                              <small className="text-danger">
                                {error.nameError}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          {/* holiday start date */}
                          <div style={{ flex: 1 }}>
                            <label className="form-label">
                              Start Date
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className={`form-control ${
                                error.startDateError ? "is-invalid" : ""
                              }`}
                              value={addHolidayForm.start_date}
                              onChange={(e) => {
                                setAddHolidayForm((prev) => ({
                                  ...prev,
                                  start_date: e.target.value,
                                }));
                                if (error.startDateError) {
                                  setError((prev) => ({
                                    ...prev,
                                    startDateError: "",
                                  }));
                                }
                              }}
                            />
                            {error.startDateError && (
                              <small className="text-danger">
                                {error.startDateError}
                              </small>
                            )}
                          </div>
                          {/* holiday end date */}
                          <div style={{ flex: 1 }}>
                            <label className="form-label">End Date</label>
                            <input
                              type="date"
                              className={"form-control"}
                              value={addHolidayForm.end_date}
                              onChange={(e) => {
                                setAddHolidayForm((prev) => ({
                                  ...prev,
                                  end_date: e.target.value,
                                }));
                              }}
                            />
                            {error.dateError && (
                              <small className="text-danger">
                                {error.dateError}
                              </small>
                            )}
                          </div>
                        </div>
                        {/* holiday description */}
                        <div name="holiday_description">
                          <label className="form-label">
                            Holiday Description
                            <span className="text-danger">*</span>
                          </label>
                          <textarea
                            rows={3}
                            className={`form-control ${
                              error.descriptionError ? "is-invalid" : ""
                            }`}
                            value={addHolidayForm.description}
                            onChange={(e) => {
                              setAddHolidayForm((prev) => ({
                                ...prev,
                                description: e.target.value,
                              }));
                              if (error.descriptionError) {
                                setError((prev) => ({
                                  ...prev,
                                  descriptionError: "",
                                }));
                              }
                            }}
                            placeholder="Optional description"
                          ></textarea>
                          {error.descriptionError && (
                            <small className="text-danger">
                              {error.descriptionError}
                            </small>
                          )}
                        </div>
                        {/* select the branches whom we have to apply */}
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Apply to Branches{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="row g-2">
                            {error.selectedBranchError && (
                              <small className="text-danger">
                                {error.selectedBranchError}
                              </small>
                            )}
                            {branches.map((branch) => (
                              <div
                                className="col-12 col-md-6 col-lg-4"
                                key={branch.id}
                              >
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`add-branch-${branch.id}`}
                                    checked={addHolidayForm.selectedBranches.includes(
                                      branch.id
                                    )}
                                    onChange={(e) => {
                                      handleBranchSelection(
                                        branch.id,
                                        e.target.checked
                                      );
                                      if (error.selectedBranchError) {
                                        setError((prev) => ({
                                          ...prev,
                                          selectedBranchError: "",
                                        }));
                                      }
                                    }}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`add-branch-${branch.id}`}
                                  >
                                    {branch.agencyname || branch.name}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div
                      className="modal-footer"
                      style={{
                        background: "#f7f8fa",
                        borderTop: "1px solid #eef2f6",
                      }}
                    >
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setShowAddForm(false)}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleAddHoliday}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            {editingHoliday ? "Edit Holiday" : "Save Holiday"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Search & Filter */}
          <div className="card border mb-2">
            <div className="card-body">
              <div className="d-flex gap-1">
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaIcons.FaSearch className="text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      value={filters?.name}
                      onChange={(e) =>
                        handleFilterChange("name", e.target.value)
                      }
                      placeholder="Search holidays..."
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filters?.branch_id}
                    onChange={(e) =>
                      handleFilterChange("branch_id", e.target.value)
                    }
                  >
                    <option value="all">All Branches</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.agencyname || branch.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button onClick={applyFilters} className="btn btn-primary">
                  Apply Filters
                </button>

                <button onClick={clearFilters} className="btn btn-light">
                  Clear All
                </button>
              </div>
            </div>
          </div>

          {/* Holidays List */}
          <div className="card border">
            <div className="card-body">
              {showSkeleton ? (
                <>
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                </>
              ) : globalHolidays?.length === 0 ? (
                <div className="text-center py-5">
                  <FaIcons.FaCalendarAlt className="fs-1 text-muted mb-3" />
                  <p className="text-muted">No holidays found</p>
                </div>
              ) : (
                <div className="row g-3">
                  {globalHolidays?.map((holiday) => (
                    <div className="col-12" key={holiday.id}>
                      <div className="card border">
                        <div className="card-body">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-3 mb-2">
                                <h6 className="mb-0">{holiday.name}</h6>

                                <small
                                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm `}
                                >
                                  {formatDate(holiday?.start_date)}
                                  <span className="mx-1">→</span>
                                  {formatDate(holiday?.end_date)}
                                </small>
                              </div>
                              {holiday.description && (
                                <p className="text-muted small mb-2">
                                  {holiday.description}
                                </p>
                              )}
                              <div className="d-flex flex-wrap gap-2">
                                {getBranchNames(holiday.branch_ids || []).map(
                                  (branchName, index) => (
                                    <span
                                      key={index}
                                      className="badge bg-success"
                                    >
                                      {branchName}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => {
                                  setEditingHoliday(holiday?.id);
                                  setShowAddForm(true);
                                  setAddHolidayForm({
                                    id: holiday?.id,
                                    name: holiday?.name,
                                    description: holiday?.description,
                                    start_date: holiday?.start_date,
                                    end_date: holiday?.end_date,
                                    selectedBranches: holiday?.branch_ids,
                                  });
                                  setError({
                                    nameError: "",
                                    startDateError: "",
                                    dateError: "",
                                    descriptionError: "",
                                    selectedBranchError: "",
                                  });
                                }}
                              >
                                <FaIcons.FaEdit />
                              </button>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDeleteHoliday(holiday.id)}
                              >
                                <FaIcons.FaTrashAlt />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Pagination Section */}
          <div className="row align-items-center mb-5 mt-2">
            <div className="col-auto">
              <select
                value={pagination?.perPage}
                onChange={handlePerPageChange}
                className="form-select"
                style={{ minWidth: 80 }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
            <div className="col-auto text-muted">
              Page {pagination.currentPage} – Showing{" "}
              {Math.min(
                pagination.perPage,
                pagination.total -
                  (pagination.currentPage - 1) * pagination.perPage
              )}{" "}
              of {pagination.total} entries
            </div>
            <div className="col">
              <ReactPaginate
                previousLabel={"Previous"}
                nextLabel={"Next"}
                breakLabel={"..."}
                pageCount={pagination.lastPage}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={handlePageChange}
                forcePage={pagination.currentPage - 1}
                containerClassName={"pagination justify-content-end mb-0"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextClassName={"page-item"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
                disabledClassName={"disabled"}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
